import type { FormEvent } from 'react';
import { useState, useEffect } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import type { TierDefinition } from '../../../../infrastructure/LoyaltyClient';

interface UpdateTierModalProps {
  show: boolean;
  onClose: () => void;
  onUpdate: (tier: string) => Promise<void>;
  currentTier: string;
  tierDefinitions?: TierDefinition[];
  isLoadingTiers?: boolean;
}

export const UpdateTierModal = ({
  show,
  onClose,
  onUpdate,
  currentTier,
  tierDefinitions = [],
  isLoadingTiers = false,
}: UpdateTierModalProps) => {
  const [selectedTier, setSelectedTier] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!show) {
      setSelectedTier('');
    }
  }, [show]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onUpdate(selectedTier);
      setSelectedTier('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const sortedTiers = tierDefinitions && tierDefinitions.length > 0 
    ? [...tierDefinitions].sort((a, b) => {
        const orderA = a.displaySettings?.displayOrder ?? a.level ?? a.rank ?? 0;
        const orderB = b.displaySettings?.displayOrder ?? b.level ?? b.rank ?? 0;
        return orderA - orderB;
      })
    : [];

  return (
    <Modal show={show} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Update Customer Tier</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <div className="mb-3">
            <strong>Current Tier:</strong> {currentTier}
          </div>

          <Form.Group className="mb-3">
            <Form.Label>New Tier</Form.Label>
            <Form.Select
              value={selectedTier}
              onChange={(e) => setSelectedTier(e.target.value)}
              required
              disabled={isLoadingTiers || isSubmitting}
            >
              <option value="">Select tier...</option>
              {isLoadingTiers ? (
                <option disabled>Loading tiers...</option>
              ) : sortedTiers.length === 0 ? (
                <option disabled>No tiers available</option>
              ) : (
                sortedTiers.map((tier) => (
                  <option key={tier.tier} value={tier.tier} disabled={currentTier === tier.tier}>
                    {tier.tier}
                  </option>
                ))
              )}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="info" type="submit" disabled={isSubmitting || !selectedTier || isLoadingTiers}>
            {isSubmitting ? 'Processing...' : 'Update Tier'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};
